// #region Imports

import { Table } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import type { DataReglementType, FactureType } from '@/types';
import { formatMonetaire } from '@/utils';

// #endregion

// #region Types

interface ModalAffectationReglementTableFooterProps {
  factures: FactureType[];
  reglements: DataReglementType[];
  montantRegle: Record<string, number | string>[];
}

// #endregion

const ModalAffectationReglementTableFooter = (props: ModalAffectationReglementTableFooterProps) => {
  const { factures, reglements, montantRegle } = props;

  // #region Déclarations

  /**
   * Calcule le montant total à encaisser en fonction des factures.
   *
   * @returns {number} Le montant total à encaisser.
   */
  const montantTotalEncaisser = React.useMemo(() => {
    let res = 0;
    _.map(factures, (facture) => {
      res += _.sumBy(facture.lignes, 'reste');
    });
    return res;
  }, [factures]);

  /**
   * Calcule le total des factures.
   *
   * @returns {number} Le total des factures.
   */
  const totalFacture = React.useMemo(() => {
    let res = 0;
    _.map(factures, (facture) => {
      res += _.sumBy(facture.lignes, 'montant');
    });
    return res;
  }, [factures]);

  /**
   * Calcule le reste à encaisser.
   *
   * @returns {number} Le reste à encaisser.
   */
  const resteAEncaisser = React.useMemo(
    () => totalFacture - _.sumBy(montantRegle, (m) => (m.montant as number) || 0),
    [totalFacture, montantRegle],
  );

  // #endregion

  return (
    <Table.Footer
      style={{
        position: 'sticky',
        bottom: -1,
        background: 'white',
        zIndex: 10,
      }}
    >
      <Table.Row>
        <Table.HeaderCell colSpan={3} />
        <Table.HeaderCell textAlign="right">
          <b>{formatMonetaire(totalFacture)}</b>
        </Table.HeaderCell>
        <Table.HeaderCell textAlign="right">
          <b>{formatMonetaire(montantTotalEncaisser)}</b>
        </Table.HeaderCell>
        {_.map(reglements, (reglement) => {
          const totalReglement = parseFloat(
            _.sumBy(
              _.filter(montantRegle, (r) => r.identifiantReglement === reglement.identifiant),
              (m) => (m.montant as number) || 0,
            ).toFixed(2),
          );

          return (
            <Table.HeaderCell
              as={Table.Cell}
              warning={totalReglement <= reglement.montant && totalReglement >= 0}
              error={totalReglement > reglement.montant || totalReglement < 0}
              textAlign="right"
              style={{ borderTop: '1px solid rgba(152,162,179,.2)' }}
            >
              <b>{formatMonetaire(totalReglement)}</b>
            </Table.HeaderCell>
          );
        })}
        <Table.HeaderCell
          textAlign="right"
          {...(_.sumBy(factures, 'montantTotal') -
            _.sumBy(montantRegle, (m) => (m.montant as number) || 0) <
            0 && {
            error: true,
            as: Table.Cell,
            style: { borderTop: '1px solid rgba(152,162,179,.2)' },
          })}
        >
          <b>{formatMonetaire(resteAEncaisser)}</b>
        </Table.HeaderCell>
      </Table.Row>
      <Table.Row>
        <Table.HeaderCell colSpan={5} textAlign="right">
          <b>Reste à répartir</b>
        </Table.HeaderCell>
        {_.map(reglements, (reglement) => {
          const totalReglement = _.sumBy(
            _.filter(montantRegle, {
              identifiantReglement: reglement.identifiant,
            }),
            (m) => (m.montant as number) || 0,
          ).toFixed(2);

          const resteRepartir = reglement.montant - parseFloat(totalReglement);

          return (
            <Table.HeaderCell
              as={Table.Cell}
              positive={resteRepartir === 0}
              warning={resteRepartir > 0}
              negative={resteRepartir < 0}
              style={{ borderTop: '1px solid rgba(152,162,179,.2)' }}
              textAlign="right"
            >
              {formatMonetaire(resteRepartir)}
            </Table.HeaderCell>
          );
        })}
        <Table.HeaderCell />
      </Table.Row>
    </Table.Footer>
  );
};

export default ModalAffectationReglementTableFooter;
